
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { supabase } from '../../lib/supabase';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { signIn, checkAdminRole, isAdmin } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Se já estiver autenticado como admin, redirecionar automaticamente
  useEffect(() => {
    if (isAdmin) {
      navigate('/admin', { replace: true });
    }
  }, [isAdmin, navigate]);

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    setError(''); // Limpa erro ao digitar
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    let loginSuccess = false;

    try {
      // RATE LIMITING: Verificar se o email pode tentar login
      console.log('[AdminLoginPage] Verificando rate limiting por email...');
      const { data: canLogin, error: rateLimitError } = await supabase.rpc('check_login_attempts', {
        p_email: formData.email.toLowerCase().trim()
      });

      if (rateLimitError) {
        console.error('[AdminLoginPage] Erro ao verificar rate limiting:', rateLimitError);
        // Continuar mesmo com erro (fallback gracioso)
      } else if (canLogin === false) {
        console.warn('[AdminLoginPage] Email bloqueado por excesso de tentativas');
        setError('Muitas tentativas de login falhadas. Por favor, aguarde 15 minutos e tente novamente.');
        setLoading(false);
        return;
      }

      console.log('[AdminLoginPage] Rate limiting OK, prosseguindo com login...');
      // Fazer login (não-bloqueante para admin); em seguida validar role explicitamente
      await signIn(formData.email, formData.password);
      const isAdminNow = await checkAdminRole();
      if (isAdminNow) {
        console.log('✅ [AdminLoginPage] Usuário é admin, redirecionando...');
        loginSuccess = true;
        navigate('/admin', { replace: true });
      } else {
        console.log('❌ [AdminLoginPage] Usuário não é admin');
        setError('Acesso negado. Você não tem privilégios de administrador.');
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao fazer login');
      setLoading(false);
    } finally {
      // RATE LIMITING: Registrar tentativa de login (sucesso ou falha)
      try {
        console.log('[AdminLoginPage] Registrando tentativa de login...');
        await supabase.rpc('register_login_attempt', {
          p_email: formData.email.toLowerCase().trim(),
          p_success: loginSuccess,
          p_ip: null,
          p_user_agent: navigator.userAgent
        });
        console.log('[AdminLoginPage] Tentativa de login registrada com sucesso');
      } catch (registerError) {
        console.warn('[AdminLoginPage] Erro ao registrar tentativa de login (não crítico):', registerError);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex flex-col items-center justify-center px-6 py-8">
      {/* Logo e Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-purple-600 rounded-3xl flex items-center justify-center mb-4 mx-auto shadow-xl">
          <i className="ri-admin-line text-3xl text-white"></i>
        </div>
        <h1 className="text-3xl font-bold text-gray-800 mb-2" style={{
          textShadow: '1px 2px 4px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.05)'
        }}>
          Acesso Administrativo
        </h1>
        <p className="text-gray-600">
          Entre com suas credenciais de administrador
        </p>
      </div>

      {/* Formulário */}
      <div className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-xl">
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center mb-3 mx-auto">
            <i className="ri-shield-keyhole-line text-purple-600 text-xl"></i>
          </div>
          <h2 className="text-xl font-semibold text-gray-800">Login Admin</h2>
          <p className="text-gray-500 text-sm mt-1">
            Acesso restrito ao painel de controle
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-4">
            <div className="flex items-center">
              <i className="ri-error-warning-line text-red-500 text-lg mr-2"></i>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="E-mail Administrativo"
            type="email"
            placeholder="admin@homeoCare.com"
            value={formData.email}
            onChange={handleInputChange('email')}
            required
          />
          
          <Input
            label="Senha"
            type="password"
            placeholder="Senha administrativa"
            value={formData.password}
            onChange={handleInputChange('password')}
            required
          />

          <Button 
            type="submit" 
            size="lg" 
            className="w-full mt-6 bg-purple-600 hover:bg-purple-700"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Verificando...
              </div>
            ) : (
              <>
                <i className="ri-login-box-line mr-2"></i>
                Entrar no Painel
              </>
            )}
          </Button>
        </form>

        {/* Credenciais de demonstração */}
        <div className="mt-6 p-4 bg-purple-50 rounded-2xl border border-purple-100">
          <h4 className="text-sm font-medium text-purple-800 mb-2">
            <i className="ri-information-line mr-1"></i>
            Credenciais de Demonstração:
          </h4>
          <div className="text-xs text-purple-700 space-y-1">
            <p><strong>E-mail:</strong> admin@homeoCare.com</p>
            <p><strong>Senha:</strong> admin123</p>
          </div>
        </div>
      </div>

      {/* Recursos Admin */}
      <div className="grid grid-cols-3 gap-4 mt-8 w-full max-w-sm">
        <div className="text-center">
          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-2 mx-auto shadow-md">
            <i className="ri-settings-3-line text-purple-500 text-xl"></i>
          </div>
          <p className="text-xs text-gray-600">Configurações</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-2 mx-auto shadow-md">
            <i className="ri-bar-chart-line text-purple-500 text-xl"></i>
          </div>
          <p className="text-xs text-gray-600">Relatórios</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-2 mx-auto shadow-md">
            <i className="ri-game-line text-purple-500 text-xl"></i>
          </div>
          <p className="text-xs text-gray-600">Gamificação</p>
        </div>
      </div>

      {/* Segurança */}
      <div className="mt-6 text-center">
        <p className="text-xs text-gray-500 flex items-center justify-center">
          <i className="ri-shield-check-line mr-1"></i>
          Conexão segura e criptografada
        </p>
      </div>
    </div>
  );
}
